import React, {FC, useEffect, useMemo, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {ApplePayD, Icon} from '@optimaxdev/icons';
import {useStripe} from '@stripe/react-stripe-js';
import {
    PaymentIntentResult,
    PaymentRequest,
    PaymentRequestPaymentMethodEvent,
} from '@stripe/stripe-js';
import {connect} from 'react-redux';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {PaymentDetailsType} from 'types/applePayType';
import {PaymentType} from 'types/PaymentMethodsType';

import {PAYMENTS} from '../../constants/paymentStep';
import {setOptionPaymentMethod as setOptionPaymentMethodAction} from '../../store/reducers/analytics/analytics';
import {
    saveApplePayPayment,
    setApplePayInnerError,
} from '../../store/reducers/paymentStep/applePay/applePay';
import {
    getApplePayOrderError,
    getPaymentDetailsWithoutShipping,
} from '../../store/selectors/paymentStep/applePay/applePay';
import {
    getIsSelectedByKey,
    getPaymentMethodByKey,
} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {PaymentApplePayButtonConnected} from './components/paymentApplePayButton/paymentApplePayButton';
import {createStripeRequest} from './utils/createStripeRequest';

import s from './paymentApplePay.scss';

type PropsType = {
    isSelected: boolean;
    method: PaymentType;
    setOptionPaymentMethod: () => void;
    saveApplePay: ({token}: {token: string}) => void;
    paymentDetails: PaymentDetailsType;
    errorMessage?: string;
    setApplePayError: (message: string) => void;
    price?: number;
};

/**
 * Payment ApplePay for checkout
 */
const PaymentApplePay: FC<PropsType> = ({
    errorMessage,
    isSelected,
    method: {code},
    paymentDetails,
    price,
    saveApplePay,
    setApplePayError,
    setOptionPaymentMethod,
}) => {
    const {form_key: formKey} = useGetCustomerData();
    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>();

    const onError = (error: string) => {
        sendDesktopAnalyticsEvent(
            CHECKOUT_PAYMENT_STEP,
            {
                message: `Error – ${error}`,
                method: 'Apple pay',
            },
            false,
        );

        setApplePayError(error || 'Something went wrong, please try again later');
    };

    const handlePayment = (
        evt: PaymentRequestPaymentMethodEvent,
        clientSecret: string,
        {error, paymentIntent}: PaymentIntentResult,
    ) => {
        if (error) {
            evt.complete('fail');
            if (error?.message) onError(error.message);
        } else {
            evt.complete('success');
            if (paymentIntent?.status === 'requires_action') {
                stripe?.confirmCardPayment(clientSecret);
            }
            setOptionPaymentMethod();
            saveApplePay({token: evt.paymentMethod.id});
        }
    };

    const paymentDetailsData = useMemo(() => {
        if (price) {
            return {
                ...paymentDetails,
                displayItems: [
                    ...paymentDetails.displayItems,
                    {
                        amount: Number((price * 100).toFixed(0)),
                        label: 'Shipping Protection',
                    },
                ],
            };
        }

        return paymentDetails;
    }, [paymentDetails, price]);

    useEffect(() => {
        if (stripe) {
            createStripeRequest({
                formKey,
                handlePayment,
                paymentDetails: paymentDetailsData,
                setApplePayError,
                setPaymentRequest,
                stripe,
            });
        }
    }, [stripe, paymentDetailsData]);

    useEffect(() => {
        if (errorMessage) {
            onError(errorMessage);
        }
    }, [errorMessage, onError]);

    if (!paymentRequest) return null;

    return (
        <PaymentWrapperConnected
            title={
                <div className={s.title}>
                    <Icon className={s.icon} icon={ApplePayD} />
                </div>
            }
            code={code}
            isSelected={isSelected}
        >
            <PaymentApplePayButtonConnected
                paymentRequest={paymentRequest}
                shippingProtectionPrice={price}
            />
        </PaymentWrapperConnected>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    errorMessage: getApplePayOrderError(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_APPLE_PAY),
    method: getPaymentMethodByKey(store, PAYMENTS.METHOD_APPLE_PAY),
    paymentDetails: getPaymentDetailsWithoutShipping(store),
});

const mapDispatchToProps = {
    saveApplePay: saveApplePayPayment,
    setApplePayError: setApplePayInnerError,
    setOptionPaymentMethod: setOptionPaymentMethodAction,
};

export const PaymentApplePayConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PaymentApplePay);
